import { useTranslation } from 'react-i18next'
import { format, parseISO } from 'date-fns'
import { cn } from '@/lib/utils'
import { calculateGainLoss } from '@/lib/services/stockPrice'
import type { ActivePosition } from '@/lib/repositories'
import type { StockQuote } from '@/lib/services/stockPrice'

interface PositionsListProps {
  positions: ActivePosition[]
  quotes: Map<string, StockQuote>
  quotesLoading: boolean
  isAdmin: boolean
  onEditPosition?: (id: string) => void
}

function formatPrice(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

const TICKER_COLORS = [
  {
    text: 'text-emerald-400',
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/20',
  },
  {
    text: 'text-cyan-400',
    bg: 'bg-cyan-500/10',
    border: 'border-cyan-500/20',
  },
  {
    text: 'text-violet-400',
    bg: 'bg-violet-500/10',
    border: 'border-violet-500/20',
  },
] as const

export function PositionsList({
  positions,
  quotes,
  quotesLoading,
  isAdmin,
  onEditPosition,
}: PositionsListProps) {
  const { t } = useTranslation()

  // Calculate totals
  let totalPnl = 0
  let totalBasis = 0
  let hasAnyQuote = false

  for (const pos of positions) {
    const quote = quotes.get(pos.ticker)
    if (quote) {
      hasAnyQuote = true
      const { value } = calculateGainLoss(quote.currentPrice, pos.buyInPrice)
      totalPnl += value
      totalBasis += pos.buyInPrice
    }
  }

  const totalPnlPercent = totalBasis > 0 ? (totalPnl / totalBasis) * 100 : 0

  return (
    <div>
      {/* Header row */}
      <div className="flex items-center justify-between px-1 pb-2 text-[10px] uppercase tracking-wider text-neutral-500">
        <span>{t('ticker')}</span>
        <div className="flex items-center gap-4 sm:gap-6">
          <span className="hidden sm:inline w-20 text-right">{t('buyIn')}</span>
          <span className="w-20 text-right">{t('current')}</span>
          <span className="w-20 text-right">{t('pnl')}</span>
        </div>
      </div>

      {/* Position rows */}
      <div className="divide-y divide-white/[0.04]">
        {positions.map((position, index) => {
          const quote = quotes.get(position.ticker)
          const gainLoss = quote
            ? calculateGainLoss(quote.currentPrice, position.buyInPrice)
            : null
          const isPositive = gainLoss ? gainLoss.value >= 0 : true
          const color = TICKER_COLORS[index % TICKER_COLORS.length]!

          return (
            <div
              key={position.id}
              className={cn(
                'flex items-center justify-between py-3 px-1',
                isAdmin &&
                  'cursor-pointer hover:bg-white/[0.02] rounded-lg transition-colors'
              )}
              onClick={() => isAdmin && onEditPosition?.(position.id)}
            >
              {/* Ticker */}
              <div className="flex items-center gap-2.5">
                <div className={cn('w-8 h-8 rounded-lg border flex items-center justify-center', color.bg, color.border)}>
                  <span className={cn('text-[10px] font-bold tracking-tight', color.text)}>
                    {position.ticker.slice(0, 4)}
                  </span>
                </div>
                <div>
                  <span className={cn('text-sm font-medium', color.text)}>
                    {position.ticker}
                  </span>
                  {position.openedAt && (
                    <p className="text-[10px] text-neutral-500 leading-tight">
                      {t('sinceDate', {
                        date: format(parseISO(position.openedAt), 'MMM d'),
                      })}
                    </p>
                  )}
                </div>
              </div>

              {/* Prices + P&L */}
              <div className="flex items-center gap-4 sm:gap-6">
                {/* Buy-in - hidden on mobile */}
                <span className="hidden sm:inline w-20 text-right text-sm font-mono text-neutral-400">
                  {formatPrice(position.buyInPrice)}
                </span>

                {/* Current price */}
                <span className="w-20 text-right text-sm font-mono text-white">
                  {quotesLoading ? (
                    <span className="inline-block w-14 h-4 bg-neutral-800 rounded animate-pulse" />
                  ) : quote ? (
                    formatPrice(quote.currentPrice)
                  ) : (
                    '--'
                  )}
                </span>

                {/* P&L */}
                <span
                  className={cn(
                    'w-20 text-right text-sm font-mono',
                    quotesLoading
                      ? ''
                      : isPositive
                        ? 'text-emerald-400'
                        : 'text-red-400'
                  )}
                >
                  {quotesLoading ? (
                    <span className="inline-block w-14 h-4 bg-neutral-800 rounded animate-pulse" />
                  ) : gainLoss ? (
                    `${isPositive ? '+' : ''}${gainLoss.percent.toFixed(2)}%`
                  ) : (
                    '--'
                  )}
                </span>
              </div>
            </div>
          )
        })}
      </div>

      {/* Summary row */}
      {hasAnyQuote && positions.length > 1 && (
        <div className="flex items-center justify-between pt-3 mt-1 border-t border-white/[0.08] px-1">
          <span className="text-xs text-neutral-400 uppercase tracking-wider">
            {t('total')}
          </span>
          <div className="flex items-center gap-4 sm:gap-6">
            <span className="hidden sm:inline w-20" />
            <span className="w-20 text-right text-sm font-mono text-white">
              {formatPrice(totalPnl)}
            </span>
            <span
              className={cn(
                'w-20 text-right text-sm font-mono',
                totalPnl >= 0 ? 'text-emerald-400' : 'text-red-400'
              )}
            >
              {totalPnl >= 0 ? '+' : ''}
              {totalPnlPercent.toFixed(2)}%
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
